import React, { useState, useRef, useEffect } from 'react';

interface TimeDropdownProps {
  selectedTime: string | null;
  onTimeChange: (time: string | null) => void;
  date: Date | null;
  generateTimeSlots: () => string[];
  formatTime: (time: string) => string;
  disabled: boolean;
}

const TimeDropdown: React.FC<TimeDropdownProps> = ({
  selectedTime,
  onTimeChange,
  date,
  generateTimeSlots,
  formatTime,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTimeSelect = (time: string) => {
    onTimeChange(time);
    setIsOpen(false);
  };

  const timeSlots = generateTimeSlots();

  return (
    <div className="custom-time-dropdown-container" ref={dropdownRef}>
      <button
        type="button"
        className="form-control custom-time-dropdown-toggle mt-2"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        {selectedTime ? formatTime(selectedTime) : 'Select a time'}
        <span className={`arrow-icon ${isOpen ? 'open' : ''}`}>&#9660;</span>
      </button>
      {isOpen && (
        <div className="custom-time-dropdown-menu">
          {timeSlots.map((slot) => {
            const now = new Date();
            const threeHoursFromNow = new Date(now.getTime() + 3 * 60 * 60 * 1000);
            const [hour, minute] = slot.split(':').map(Number);

            const slotTime = new Date(date || 0);
            slotTime.setHours(hour, minute, 0, 0);

            let isDisabled = false;
            if (date && date.toDateString() === now.toDateString() && slotTime < threeHoursFromNow) {
              isDisabled = true;
            }

            return (
              <div
                key={slot}
                className={`custom-time-dropdown-item ${isDisabled ? 'disabled' : ''} ${selectedTime === slot ? 'selected' : ''}`}
                onClick={() => !isDisabled && handleTimeSelect(slot)}
              >
                {formatTime(slot)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TimeDropdown;
